<template>
  <div class="onboarding-container">
    <router-view />
  </div>
</template>

<script>
export default {
  name: 'Onboarding',

  mounted() {
    // Mark user as actively in onboarding
    this.$store.commit('onboarding/SET_IS_ONBOARDING', true)

    // Restore state from localStorage if Vuex was reset (e.g. page refresh)
    this.restoreState()

    this.fetchDashboardPages()

    // Persist state when user refreshes or navigates away
    window.addEventListener('beforeunload', this.saveState)
  },

  beforeDestroy() {
    window.removeEventListener('beforeunload', this.saveState)
  },

  methods: {
    /**
     * Fetch all pages from dashboard and mark as pre-existing in onboarding store.
     * This ensures quota counting includes ALL dashboard pages, not just selected networks.
     */
    async fetchDashboardPages() {
      // Skip if already populated (e.g. after page refresh)
      if (Object.keys(this.$store.state.onboarding.preExistingPageIds).some(
        network => this.$store.state.onboarding.preExistingPageIds[network].length > 0
      )) {
        console.log('[Onboarding] preExistingPageIds already populated, skipping fetch')
        return
      }

      try {
        const apiUrl = `${process.env.VUE_APP_API_HOST}/pages`
        const token = localStorage.getItem('token')
        const response = await this.$http.get(apiUrl, {
          headers: { Authorization: `Bearer ${token}` }
        })

        if (response && response.data && response.data.pages) {
          const pagesByNetwork = {}
          
          // Group pages by network
          response.data.pages.forEach(page => {
            const network = page.network || 'unknown'
            if (!pagesByNetwork[network]) {
              pagesByNetwork[network] = []
            }
            // Use network_id if available (preferred), fallback to id
            const pageId = page.network_id || page.id
            if (pageId && !pagesByNetwork[network].includes(pageId)) {
              pagesByNetwork[network].push(pageId)
            }
          })

          // Mark all dashboard pages as pre-existing
          Object.entries(pagesByNetwork).forEach(([network, pageIds]) => {
            this.$store.commit('onboarding/SET_PRE_EXISTING_PAGE_IDS', {
              network,
              pageIds
            })
          })

          console.log('[Onboarding] Marked dashboard pages as pre-existing:', pagesByNetwork)
        }
      } catch (error) {
        console.error('[Onboarding] Failed to fetch dashboard pages:', error)
        // Non-blocking error - onboarding can continue without this
      }
    },

    /**
     * Persist complete onboarding Vuex state to localStorage before page unload.
     * This covers steps 1-2 (networks/features) which don't have their own persistence.
     * Steps 3+ already persist via their own persistOnboardingState() calls.
     */
    saveState() {
      const onboardingState = this.$store.state.onboarding
      localStorage.setItem('onboarding_state', JSON.stringify(onboardingState))
    },

    /**
     * Restore onboarding Vuex state from localStorage after a page refresh.
     * Only restores if the store is in its initial empty state (i.e. after a refresh).
     */
    restoreState() {
      const savedStateStr = localStorage.getItem('onboarding_state')
      if (!savedStateStr) return

      try {
        const savedState = JSON.parse(savedStateStr)
        const store = this.$store

        // Only restore if Vuex is in its default empty state (post-refresh)
        const storeIsEmpty =
          store.state.onboarding.selectedNetworks.length === 0 &&
          store.state.onboarding.selectedFeatures.length === 0

        if (!storeIsEmpty) {
          console.log('[Onboarding] Store already populated, skipping restore')
          return
        }

        console.log('[Onboarding] Restoring state from localStorage')

        // Restore needs data
        if (savedState.needsPages != null && savedState.needsUsers != null) {
          store.commit('onboarding/SET_NEEDS', {
            pages: savedState.needsPages,
            users: savedState.needsUsers
          })
        }

        // Restore networks and features (steps 1-2)
        if (savedState.selectedNetworks && savedState.selectedNetworks.length > 0) {
          store.commit('onboarding/SET_NETWORKS', savedState.selectedNetworks)
        }
        if (savedState.selectedFeatures && savedState.selectedFeatures.length > 0) {
          store.commit('onboarding/SET_FEATURES', savedState.selectedFeatures)
        }
        if (savedState.currentStep) {
          store.commit('onboarding/SET_STEP', savedState.currentStep)
        }

        // Restore connection data (steps 3+)
        if (savedState.connectedPages) {
          store.commit('onboarding/SET_CONNECTED_PAGES', savedState.connectedPages)
        }
        if (savedState.selectedPageIds) {
          store.commit('onboarding/SET_ALL_SELECTED_PAGE_IDS', savedState.selectedPageIds)
        }
        if (savedState.preExistingPageIds) {
          Object.entries(savedState.preExistingPageIds).forEach(([network, pageIds]) => {
            store.commit('onboarding/SET_PRE_EXISTING_PAGE_IDS', { network, pageIds })
          })
        }
        if (savedState.nativeConnections) {
          store.commit('onboarding/SET_NATIVE_CONNECTIONS', savedState.nativeConnections)
        }
        if (savedState.newlyConnectedPageIds) {
          store.commit('onboarding/SET_NEWLY_CONNECTED_PAGE_IDS', savedState.newlyConnectedPageIds)
        }

        console.log('[Onboarding] State restored successfully:', {
          networks: savedState.selectedNetworks?.length || 0,
          features: savedState.selectedFeatures?.length || 0,
          step: savedState.currentStep
        })
      } catch (error) {
        console.error('[Onboarding] Failed to restore state from localStorage:', error)
        localStorage.removeItem('onboarding_state')
      }
    }
  }
}
</script>

<style scoped lang="scss">
.onboarding-container {
  width: 100%;
  min-height: 100vh;
  overflow-x: hidden;
}
</style>
